import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";

/**
 * QAStickyConverter. sticky-bottom conversion bar on Q&A pages.
 *
 * Behaviour:
 * - Hidden initially.
 * - Slides up from bottom once user has scrolled past `triggerPct` of the
 * document height (default 35%). This guarantees the user has actually
 * read some of the answer before we ask anything of them.
 * - Dismissible with ×. dismissal is per-slug so users see it again on
 * the NEXT Q&A (high-intent users browsing multiple = high-intent buyers).
 * - Stays visible once shown (no scroll-up hide flicker).
 *
 * Calibrated to be visible but not desperate. calm-mode visual treatment.
 */
export default function QAStickyConverter({ slug, ctaText = "Try Compass on your case" }) {
 const [visible, setVisible] = useState(false);
 const [dismissed, setDismissed] = useState(false);

 useEffect(() => {
 // Check if user already dismissed for THIS slug.
 try {
 const dismissedSlugs = JSON.parse(localStorage.getItem("tt_qa_sticky_dismissed") || "[]");
 if (dismissedSlugs.includes(slug)) {
 setDismissed(true);
 return;
 }
 } catch { /* localStorage disabled. show anyway */ }

 const onScroll = () => {
 // Require an actual user scroll. don't auto-reveal on mount even when
 // the page is short enough that mount-position is already past 35%.
 if (window.scrollY <= 0) return;
 const scrolled = window.scrollY + window.innerHeight;
 const total = document.body.scrollHeight;
 const pct = total > 0 ? scrolled / total : 0;
 if (pct >= 0.35) setVisible(true);
 };
 window.addEventListener("scroll", onScroll, { passive: true });
 return () => window.removeEventListener("scroll", onScroll);
 }, [slug]);

 const dismiss = () => {
 try {
 const list = JSON.parse(localStorage.getItem("tt_qa_sticky_dismissed") || "[]");
 if (!list.includes(slug)) list.push(slug);
 // Cap the list at the last 50 slugs so it doesn't grow forever.
 localStorage.setItem("tt_qa_sticky_dismissed", JSON.stringify(list.slice(-50)));
 } catch { /* no-op */ }
 setDismissed(true);
 };

 if (dismissed || !visible) return null;

 return (
 <div
 className="fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 pointer-events-none"
 data-testid="qa-sticky-converter"
 >
 <div className="max-w-3xl mx-auto pointer-events-auto">
 <div
 className="bg-white border-t-2 border-t-[#1B6C62] border-x border-b border-[#E5E4DE] shadow-xl px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4"
 style={{ animation: "qa-slide-up 0.3s ease-out" }}
 >
 <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-[#E6F5F2] border border-[#1B6C62]/30 flex-shrink-0">
 <Sparkles size={15} strokeWidth={1.75} className="text-[#1B6C62]" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="font-serif text-sm sm:text-base text-[#0D2B52] leading-tight">
 <span className="hidden sm:inline">Was this useful? </span>
 <span className="font-medium">Try Compass on your case →</span>
 </div>
 <div className="text-[11px] sm:text-xs text-[#6B7280] mt-0.5">
 Personalised answer for your tenancy. Start free.
 </div>
 </div>
 <Link
 to="/register"
 className="btn-primary inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-2 sm:px-4 flex-shrink-0 whitespace-nowrap"
 data-testid="qa-sticky-cta"
 >
 <span className="hidden sm:inline">{ctaText}</span>
 <span className="sm:hidden">Start free</span>
 <ArrowRight size={12} strokeWidth={1.75} />
 </Link>
 <button
 onClick={dismiss}
 className="btn-ghost p-1 flex-shrink-0 -mr-1"
 title="Dismiss"
 data-testid="qa-sticky-dismiss"
 >
 <X size={14} strokeWidth={1.5} className="text-[#6B7280]" />
 </button>
 </div>
 </div>
 <style>{`@keyframes qa-slide-up { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
 </div>
 );
}
